import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Mail, Shield, DatabaseBackup, Building2, Send, History } from "lucide-react";
import { Layout } from "../../components/layout/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Field, NativeSelect, Panel } from "../../components/common";
import UsersTab from "./UsersTab";
import BackupTab from "./BackupTab";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";

function CentreMailTab() {
  const { refreshCentre } = useAuth();
  const [centre, setCentre] = useState({ nom: "", adresse: "", telephone: "", email: "" });
  const [smtp, setSmtp] = useState({});
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/centre").then(({ data }) => setCentre(data));
    api.get("/admin/site-smtp").then(({ data }) => setSmtp({ ...data, smtp_password: "" }));
  }, []);

  const saveCentre = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/admin/centre", centre);
      setCentre(data);
      refreshCentre();
      toast.success("Informations du centre enregistrées");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveSmtp = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/admin/site-smtp", smtp);
      setSmtp({ ...data, smtp_password: "" });
      toast.success("Paramètres e-mail enregistrés");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const test = async () => {
    setBusy(true);
    try {
      await api.post("/admin/site-smtp/test", { to_email: testTo });
      toast.success(`E-mail de test envoyé à ${testTo}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const s = (k) => (e) => setSmtp({ ...smtp, [k]: e.target.value });

  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <Panel title="Centre de formation" icon={Building2}>
        <form onSubmit={saveCentre} className="space-y-3">
          <Field label="Nom du centre" hint="Affiché sur l'écran de connexion, dans le menu et en signature des e-mails."><Input value={centre.nom} onChange={(e) => setCentre({ ...centre, nom: e.target.value })} data-testid="centre-nom" /></Field>
          <Field label="Adresse"><Input value={centre.adresse} onChange={(e) => setCentre({ ...centre, adresse: e.target.value })} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Téléphone"><Input value={centre.telephone} onChange={(e) => setCentre({ ...centre, telephone: e.target.value })} /></Field>
            <Field label="E-mail de contact"><Input type="email" value={centre.email} onChange={(e) => setCentre({ ...centre, email: e.target.value })} /></Field>
          </div>
          <Button type="submit">Enregistrer</Button>
        </form>
      </Panel>
      <Panel title="E-mails (SMTP)" icon={Mail}>
        <form onSubmit={saveSmtp} className="space-y-3">
          <p className="text-xs text-muted-foreground">Utilisé pour : identifiants d'accès, convocations aux cours, fiches de suivi, sauvegardes. Également paramétrable depuis le panel.</p>
          <div className="grid gap-3 sm:grid-cols-[1fr_110px_150px]">
            <Field label="Serveur SMTP"><Input value={smtp.smtp_host || ""} onChange={s("smtp_host")} placeholder="mail.infomaniak.com" /></Field>
            <Field label="Port"><Input value={smtp.smtp_port || ""} onChange={s("smtp_port")} placeholder="587" /></Field>
            <Field label="Sécurité">
              <NativeSelect value={smtp.smtp_secure || "starttls"} onChange={s("smtp_secure")}><option value="starttls">STARTTLS (587)</option><option value="ssl">SSL (465)</option></NativeSelect>
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Utilisateur"><Input value={smtp.smtp_user || ""} onChange={s("smtp_user")} autoComplete="off" /></Field>
            <Field label="Mot de passe" hint={smtp.smtp_password_set ? "Déjà enregistré — laisser vide pour le conserver" : ""}><Input type="password" value={smtp.smtp_password || ""} onChange={s("smtp_password")} autoComplete="new-password" /></Field>
            <Field label="E-mail d'expédition"><Input value={smtp.smtp_from_email || ""} onChange={s("smtp_from_email")} /></Field>
            <Field label="Nom d'expédition"><Input value={smtp.smtp_from_name || ""} onChange={s("smtp_from_name")} placeholder="Nom du centre par défaut" /></Field>
          </div>
          <Button type="submit">Enregistrer</Button>
        </form>
        <div className="mt-5 pt-4 border-t border-border flex flex-wrap gap-2 items-end">
          <Field label="Envoyer un e-mail de test à" className="flex-1 min-w-[200px]"><Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} /></Field>
          <Button variant="outline" onClick={test} disabled={!testTo || busy}><Send size={14} /> Tester</Button>
        </div>
      </Panel>
    </div>
  );
}

function SecurityTab() {
  const [settings, setSettings] = useState(null);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/admin/security-settings").then(({ data }) => setSettings(data));
    api.get("/admin/auth-events", { params: { limit: 150 } }).then(({ data }) => setEvents(data));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/admin/security-settings", settings);
      setSettings(data);
      toast.success("Réglages de sécurité enregistrés");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const REASONS = {
    connexion_reussie: "Connexion réussie", connexion_reussie_badge: "Connexion par badge", pin_incorrect: "PIN incorrect",
    pin_incorrect_blocage: "PIN incorrect — blocage", compte_bloque: "Compte bloqué", matricule_inconnu: "Matricule inconnu",
    badge_inconnu: "Badge inconnu", acces_eleve_hors_periode: "Élève hors période d'accès",
  };
  return (
    <div className="grid gap-5 xl:grid-cols-[380px_1fr]">
      <Panel title="Protection de la connexion" icon={Shield}>
        {settings && (
          <form onSubmit={save} className="space-y-3">
            <Field label="Tentatives avant blocage"><Input type="number" min="1" max="20" value={settings.max_attempts} onChange={(e) => setSettings({ ...settings, max_attempts: Number(e.target.value) })} /></Field>
            <Field label="Durée de blocage (minutes)"><Input type="number" min="1" max="1440" value={settings.lockout_minutes} onChange={(e) => setSettings({ ...settings, lockout_minutes: Number(e.target.value) })} /></Field>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={settings.exponential} onCheckedChange={(v) => setSettings({ ...settings, exponential: !!v })} /> Doubler la durée à chaque nouveau blocage</label>
            <Field label="Plafond (minutes)"><Input type="number" min="1" max="1440" value={settings.lockout_max_minutes} onChange={(e) => setSettings({ ...settings, lockout_max_minutes: Number(e.target.value) })} /></Field>
            <Button type="submit">Enregistrer</Button>
          </form>
        )}
      </Panel>
      <Panel title="Journal des connexions" icon={History} bodyClassName="p-0">
        <div className="max-h-[520px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground sticky top-0">
              <tr><th className="text-left font-medium px-4 py-2">Date</th><th className="text-left font-medium px-4 py-2">Matricule</th><th className="text-left font-medium px-4 py-2">Résultat</th><th className="text-left font-medium px-4 py-2">IP</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">{fmtDateTime(e.created_at)}</td>
                  <td className="px-4 py-2"><code>{e.matricule}</code></td>
                  <td className={`px-4 py-2 ${e.success ? "text-emerald-600" : "text-red-600"}`}>{REASONS[e.reason] || e.reason}</td>
                  <td className="px-4 py-2 text-muted-foreground">{e.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

export default function AdminPage() {
  return (
    <Layout title="Administration">
      <Tabs defaultValue="utilisateurs">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="utilisateurs" className="gap-1.5" data-testid="admin-tab-users"><Users size={14} /> Utilisateurs & accès</TabsTrigger>
          <TabsTrigger value="centre" className="gap-1.5" data-testid="admin-tab-centre"><Mail size={14} /> Centre & e-mails</TabsTrigger>
          <TabsTrigger value="securite" className="gap-1.5"><Shield size={14} /> Sécurité connexion</TabsTrigger>
          <TabsTrigger value="sauvegardes" className="gap-1.5"><DatabaseBackup size={14} /> Sauvegardes</TabsTrigger>
        </TabsList>
        <TabsContent value="utilisateurs"><UsersTab /></TabsContent>
        <TabsContent value="centre"><CentreMailTab /></TabsContent>
        <TabsContent value="securite"><SecurityTab /></TabsContent>
        <TabsContent value="sauvegardes"><BackupTab /></TabsContent>
      </Tabs>
    </Layout>
  );
}
